import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { FC } from 'react';
import { formatMoney } from 'utils/helpers';

import { getEntryFields } from '../../utils/entry-fields';
import { getEntryValues } from '../../utils/entry-values';
import type { DisbursmentRowProps } from '../disbursmentRow';
import DisbursmentsAccountedCell from '../disbursmentsAccounted';
import DisbursmentsBtns from '../disbursmentsBtns';
import { InvolvedPartiesList } from './involvedPartiesList';
import { LabeledValue } from './labeledValue';
import { useStyles } from './styles';

const PLACEHOLDER = '------';

// Phone/tablet presentation of one ledger entry. Same data and callbacks as DisbursmentRow.
export const DisbursmentCard: FC<DisbursmentRowProps> = ({
  disbursmentItem,
  handleDeleteEntry,
  handleConfirmDelete,
  t,
  kindEntry,
  handleModals,
  handleUpdatePosted,
  itemsCollapsed,
  setItemsCollapsed,
  openDeleteAssociationModal,
}) => {
  const { _id, accounted_for, code, description, involved_parties, posted } = disbursmentItem;
  const classes = useStyles({ code });
  const fields = getEntryFields(kindEntry);
  const {
    displayedAmount,
    sumAmountAccounted,
    isReceipt,
    methodLabel,
    entryDateLabel,
  } = getEntryValues(disbursmentItem);

  const isItemCollapsed = itemsCollapsed.some((itemId) => itemId === _id);
  const titleId = `disbursment-card-${_id}`;
  const partiesId = `${titleId}-parties`;

  const editButton = (
    <IconButton
      className={classes.edit}
      aria-label={`${t('disbursments:edit')} ${description}`}
      onClick={() => handleModals('edit', _id)}
      size="large"
    >
      <FontAwesomeIcon icon="edit" size="sm" />
    </IconButton>
  );

  return (
    <Paper component="article" variant="outlined" className={classes.card} aria-labelledby={titleId}>
      <div className={clsx(classes.header, { [classes.headerCentered]: fields.canEditAmount })}>
        <Typography id={titleId} className={classes.description}>
          {description}
        </Typography>
        {fields.showAmount && (
          <div className={classes.amount}>
            {fields.canEditAmount && editButton}
            <Typography variant="body2" className={classes.money}>
              {formatMoney(displayedAmount)}
            </Typography>
          </div>
        )}
      </div>

      {fields.showMethodAndDate && (
        <div className={classes.grid}>
          <LabeledValue label={t('disbursments:method')}>
            <Typography variant="body2" component="span" className={classes.method}>
              {methodLabel}
            </Typography>
          </LabeledValue>
          <LabeledValue label={t('disbursments:date')}>
            <Typography variant="body2" component="span" className={classes.value}>
              {entryDateLabel}
            </Typography>
          </LabeledValue>
        </div>
      )}

      {fields.showDebitCredit && (
        <div className={classes.grid}>
          <LabeledValue label={t('disbursments:debit')}>
            {code === 'net_proceeds' && editButton}
            <Typography
              variant="body2"
              className={clsx(classes.money, { [classes.placeholder]: isReceipt })}
            >
              {!isReceipt ? formatMoney(sumAmountAccounted) : PLACEHOLDER}
            </Typography>
          </LabeledValue>
          <LabeledValue label={t('disbursments:credit')}>
            <Typography
              variant="body2"
              className={clsx(classes.money, { [classes.placeholder]: !isReceipt })}
            >
              {isReceipt ? formatMoney(sumAmountAccounted) : PLACEHOLDER}
            </Typography>
          </LabeledValue>
        </div>
      )}

      {fields.showActions && (
        <>
          <div className={classes.section}>
            <FormControlLabel
              className={classes.posted}
              labelPlacement="start"
              label={
                <Typography variant="caption" className={classes.sectionLabel}>
                  {t('disbursments:posted')}
                </Typography>
              }
              control={(
                <Switch
                  checked={posted}
                  onChange={() => {
                    handleConfirmDelete(
                      true,
                      () => handleUpdatePosted(_id, !posted),
                      t('disbursments:titlePosted'),
                      t('disbursments:confirmationMessagePosted')
                    );
                  }}
                />
              )}
            />
            {accounted_for && accounted_for.length > 0 && (
              <>
                <Typography variant="caption" className={classes.sectionLabel}>
                  {t('disbursments:accounted-for')}
                </Typography>
                <DisbursmentsAccountedCell accounted={accounted_for} t={t} posted={posted} />
              </>
            )}
          </div>

          <div className={classes.actions}>
            <div className={classes.actionGroup}>
              <IconButton
                aria-label={t('disbursments:parties')}
                aria-expanded={isItemCollapsed}
                aria-controls={partiesId}
                onClick={() => {
                  setItemsCollapsed((prevItemList) => {
                    if (isItemCollapsed) return prevItemList.filter((itemId) => itemId !== _id);
                    return [...prevItemList, _id];
                  });
                }}
                size="large"
              >
                <FontAwesomeIcon
                  icon={isItemCollapsed ? 'arrow-alt-circle-up' : 'arrow-alt-circle-down'}
                  size="sm"
                />
              </IconButton>
              <IconButton
                aria-label={t('accounting:add-association')}
                onClick={() => handleModals('addAssociation', '', disbursmentItem)}
                size="large"
              >
                <FontAwesomeIcon icon="plus-circle" size="sm" />
              </IconButton>
            </div>
            <div className={classes.actionGroup}>
              <DisbursmentsBtns
                handleDeleteEntry={() => handleDeleteEntry(_id)}
                disbursmentItem={disbursmentItem}
                handleConfirmDelete={handleConfirmDelete}
                t={t}
                handleModals={handleModals}
              />
            </div>
          </div>

          <Collapse in={isItemCollapsed} timeout="auto" unmountOnExit>
            <div id={partiesId}>
              <InvolvedPartiesList
                parties={involved_parties || []}
                onEditParty={(party) => handleModals('addAssociation', '', disbursmentItem, party)}
                onDeleteParty={(party) => openDeleteAssociationModal(_id, party)}
                t={t}
              />
            </div>
          </Collapse>
        </>
      )}
    </Paper>
  );
};
